// Same scopes as requested via Firebase's GoogleAuthProvider (see src/services/authProvider.ts)
import { useUserStore } from '@/stores/user'
import { logger } from '@/utils/logger'

const SCOPES = [
    'profile',
    'email',
    'https://www.googleapis.com/auth/drive.activity.readonly',
    'https://www.googleapis.com/auth/drive.readonly',
    'https://www.googleapis.com/auth/drive.metadata.readonly',
    'https://www.googleapis.com/auth/contacts.readonly',
    'https://www.googleapis.com/auth/contacts.other.readonly',
].join(' ')

// Singleton state
let accessToken: string | null = null
let expiresAt: number | null = null

let gisScriptPromise: Promise<void> | null = null
let tokenClient: google.accounts.oauth2.TokenClient | null = null
// resolves the in-flight requestAccessToken() call from the token client's callback
let pendingResolve: ((token: string | null) => void) | null = null
// set by the callback when the returned token belongs to a different account than expected
let lastTokenMismatch = false
// shared by concurrent callers so only one GIS request runs at a time
let inFlightRefresh: Promise<void> | null = null

function setAccessToken (token: string, newExpiresAt: number) {
    accessToken = token
    expiresAt = newExpiresAt
}

function clearToken () {
    accessToken = null
    expiresAt = null
}

// GIS's login_hint is only a hint: with prompt:'' it can silently return a token for whichever
// Google account is active in the browser, so the actual owner must be verified after the fact.
async function fetchTokenEmail (token: string): Promise<string | null> {
    try {
        const response = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
            headers: { Authorization: `Bearer ${token}` },
        })

        if (!response.ok) {
            return null
        }

        const data = await response.json() as { email?: string }
        return data.email ?? null
    } catch (error) {
        logger.error('Error fetching Google token owner info:', error)
        return null
    }
}

function loadGisScript (): Promise<void> {
    if (gisScriptPromise) {
        return gisScriptPromise
    }

    gisScriptPromise = new Promise((resolve, reject) => {
        if (window.google?.accounts?.oauth2) {
            resolve()
            return
        }

        const script = document.createElement('script')
        script.src = 'https://accounts.google.com/gsi/client'
        script.async = true
        script.defer = true
        script.addEventListener('load', () => resolve())
        script.addEventListener('error', () => reject(new Error('Failed to load Google Identity Services script')))
        document.head.append(script)
    })

    return gisScriptPromise
}

async function getTokenClient (): Promise<google.accounts.oauth2.TokenClient> {
    await loadGisScript()

    if (!tokenClient) {
        tokenClient = google.accounts.oauth2.initTokenClient({
            client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID,
            scope: SCOPES,
            callback: async response => {
                // OAuth-level errors
                if (response.error) {
                    logger.error('Error refreshing Google access token:', response.error_description || response.error)
                    lastTokenMismatch = false
                    clearToken()
                    pendingResolve?.(null)
                    pendingResolve = null
                    return
                }

                const newExpiresAt = Math.floor(Date.now() / 1000) + Number(response.expires_in) - 5 * 60
                const expectedEmail = useUserStore().userEmail
                const tokenEmail = await fetchTokenEmail(response.access_token)

                if (expectedEmail && tokenEmail && tokenEmail.toLowerCase() !== expectedEmail.toLowerCase()) {
                    logger.error(`Google access token belongs to "${tokenEmail}", expected "${expectedEmail}" - discarding token`)
                    lastTokenMismatch = true
                    clearToken()
                    pendingResolve?.(null)
                } else {
                    lastTokenMismatch = false
                    setAccessToken(response.access_token, newExpiresAt)
                    logger.log('Google access token refreshed successfully for', tokenEmail)
                    pendingResolve?.(response.access_token)
                }
                pendingResolve = null
            },
            error_callback: error => {
                // Runtime/Browser-level errors
                // Optional. The JavaScript function that handles some non-OAuth errors,
                // such as the popup window is failed to open; or closed before an OAuth response is returned.
                // https://developers.google.com/identity/oauth2/web/reference/js-reference
                logger.error('Error refreshing Google access token:', error)
                clearToken()
                pendingResolve?.(null)
                pendingResolve = null
            },
        })
    }

    return tokenClient
}

export function useGoogleAccessToken () {
    const userStore = useUserStore()

    // ignoreExpireTime forces a refresh even if the cached token looks unexpired (e.g. Google rejected it with a 401)
    async function getAccessToken (ignoreExpireTime = false) {
        if (ignoreExpireTime || !accessToken || (expiresAt && Math.floor(Date.now() / 1000) >= expiresAt)) {
            await refreshAccessToken()
        }

        return accessToken
    }

    // Concurrent callers join the in-flight request: a second requestAccessToken() would overwrite
    // pendingResolve (leaving the first caller hanging) and trigger a duplicate token flow
    function refreshAccessToken (): Promise<void> {
        inFlightRefresh ??= requestAccessToken().finally(() => {
            inFlightRefresh = null
        })

        return inFlightRefresh
    }

    async function requestAccessToken (forceAccountSelection = false) {
        try {
            const client = await getTokenClient()
            await new Promise<string | null>(resolve => {
                pendingResolve = resolve
                logger.log('Requesting Google access token refresh... email:', userStore.userEmail)

                client.requestAccessToken({
                    // non-intrusive by default: silently refreshes without prompting when possible
                    prompt: forceAccountSelection ? 'select_account' : '',
                    login_hint: userStore.userEmail ?? undefined,
                })
            })

            // the silent refresh returned a token for the wrong Google account: retry once,
            // forcing the account chooser so the user can explicitly pick the right account
            if (lastTokenMismatch && !forceAccountSelection) {
                await requestAccessToken(true)
            }
        } catch (error) {
            logger.error('Error refreshing Google access token:', error)
            clearToken()
        }
    }

    const accessTokenFn = (ignoreExpireTime = false) => getAccessToken(ignoreExpireTime)

    return { accessTokenFn, setAccessToken }
}
